import type { WallpaperConfig } from '../config/schema';

export const AUTO_CLOCK_FRACTION = 0.32;
export const AUTO_BOTTOM_FRACTION = 0.1;

export function resolveSafeAreas(
  padding: WallpaperConfig['padding'],
  device: WallpaperConfig['device'],
): { clockReserved: number; bottomReserved: number } {
  return {
    clockReserved:
      padding.clockOffset === 'auto'
        ? Math.round(device.height * AUTO_CLOCK_FRACTION)
        : padding.clockOffset,
    bottomReserved:
      padding.bottomOffset === 'auto'
        ? Math.round(device.height * AUTO_BOTTOM_FRACTION)
        : padding.bottomOffset,
  };
}

export interface GridLayoutInput {
  width: number;
  height: number;
  paddingH: number;
  paddingV: number;
  rows: number;
  cols: number;
  topReserved?: number;
  bottomReserved?: number;
  leftReserved?: number;
  rightReserved?: number;
  groupEvery?: number;
  groupGapRatio?: number;
  groupEveryCol?: number;
  colGroupGapRatio?: number;
  /** Cap max(pitchX, pitchY) / min(pitchX, pitchY) at this value. Prevents one
   *  axis from stretching far beyond the other when row/col counts are very
   *  asymmetric (e.g. monthly's 7 cols × 5 rows on a tall portrait device).
   *  Undefined = no clamp; the grid fills both inner dimensions. */
  maxAspectRatio?: number;
}

export interface GridLayout {
  /** min(pitchX, pitchY); use for sizing dot radius and labels. */
  pitch: number;
  pitchX: number;
  pitchY: number;
  dotRadius: number;
  originX: number;
  originY: number;
  /** Vertical row-group spacing, derived from pitchY. */
  groupGap: number;
  /** Horizontal column-group spacing, derived from pitchX. */
  colGroupGap: number;
  innerWidth: number;
  innerHeight: number;
  cellPosition: (row: number, col: number) => { cx: number; cy: number };
}

export const DOT_RADIUS_FACTOR = 0.45;

export function computeDotGrid(input: GridLayoutInput): GridLayout {
  const {
    width,
    height,
    paddingH,
    paddingV,
    rows,
    cols,
    topReserved = 0,
    bottomReserved = 0,
    leftReserved = 0,
    rightReserved = 0,
    groupEvery = 0,
    groupGapRatio = 0.6,
    groupEveryCol = 0,
    colGroupGapRatio = 0.6,
    maxAspectRatio,
  } = input;

  const innerWidth = width - 2 * paddingH - leftReserved - rightReserved;
  const innerHeight = height - 2 * paddingV - topReserved - bottomReserved;

  const rowGroupCount = groupEvery > 0 ? Math.max(0, Math.floor((rows - 1) / groupEvery)) : 0;
  const colGroupCount = groupEveryCol > 0 ? Math.max(0, Math.floor((cols - 1) / groupEveryCol)) : 0;

  let pitchX = innerWidth / (cols + colGroupCount * colGroupGapRatio);
  let pitchY = innerHeight / (rows + rowGroupCount * groupGapRatio);
  if (maxAspectRatio !== undefined && maxAspectRatio > 1) {
    if (pitchY > pitchX * maxAspectRatio) pitchY = pitchX * maxAspectRatio;
    else if (pitchX > pitchY * maxAspectRatio) pitchX = pitchY * maxAspectRatio;
  }
  const pitch = Math.min(pitchX, pitchY);
  const dotRadius = pitch * DOT_RADIUS_FACTOR;
  const groupGap = pitchY * groupGapRatio;
  const colGroupGap = pitchX * colGroupGapRatio;

  const usedW = pitchX * cols + colGroupCount * colGroupGap;
  const usedH = pitchY * rows + rowGroupCount * groupGap;

  const originX = paddingH + leftReserved + (innerWidth - usedW) / 2 + pitchX / 2;
  const originY = paddingV + topReserved + (innerHeight - usedH) / 2 + pitchY / 2;

  const cellPosition = (row: number, col: number) => {
    const rowGroupOffset = groupEvery > 0 ? Math.floor(row / groupEvery) * groupGap : 0;
    const colGroupOffset = groupEveryCol > 0 ? Math.floor(col / groupEveryCol) * colGroupGap : 0;
    return {
      cx: originX + col * pitchX + colGroupOffset,
      cy: originY + row * pitchY + rowGroupOffset,
    };
  };

  return {
    pitch,
    pitchX,
    pitchY,
    dotRadius,
    originX,
    originY,
    groupGap,
    colGroupGap,
    innerWidth,
    innerHeight,
    cellPosition,
  };
}
